import { useNavigationStore } from '@/stores/navigation';

import { PlatformGroup } from './PlatformGroup';
import { ServicesCounter } from './ServicesCounter';
import { SidebarHeader } from './SidebarHeader';

import type { ReactNode } from 'react';

const WIDTH = 240,
  COLLAPSED = 64;

const asideStyle = (w: number): React.CSSProperties => ({
  width: w,
  minWidth: w,
  background: 'var(--surface)',
  borderColor: 'var(--border-subtle)',
});

const baseCls =
  'fixed md:sticky top-0 h-screen border-r flex flex-col z-50 transition-transform md:transition-[width,min-width] duration-200 overflow-hidden md:translate-x-0';

function MobileOverlay(): ReactNode {
  const { mobileMenuOpen, setMobileMenu } = useNavigationStore();
  if (!mobileMenuOpen) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 md:hidden"
      onClick={() => setMobileMenu(false)}
    />
  );
}

function SidebarNav({ collapsed }: { collapsed: boolean }): ReactNode {
  const platforms = useNavigationStore((s) => s.platforms);
  return (
    <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
      {platforms.map((p) => (
        <PlatformGroup key={p.id} platform={p} collapsed={collapsed} />
      ))}
    </nav>
  );
}

export function Sidebar(): ReactNode {
  const { sidebarCollapsed, mobileMenuOpen, toggleSidebar, setMobileMenu } =
    useNavigationStore();
  const width = sidebarCollapsed ? COLLAPSED : WIDTH;
  const cls = `${baseCls} ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`;

  return (
    <>
      <MobileOverlay />
      <aside style={asideStyle(width)} className={cls}>
        <SidebarHeader
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          onMobileClose={() => setMobileMenu(false)}
        />
        <SidebarNav collapsed={sidebarCollapsed} />
        <ServicesCounter collapsed={sidebarCollapsed} />
      </aside>
    </>
  );
}
